import { ChangeDetectorRef, Component, Input, OnInit } from '@angular/core';
import { LabelCollection, Labels } from '@utils/labels';
import { Meta, Title } from '@angular/platform-browser';
import { Page, Reference } from '@bloomreach/spa-sdk';
import { DomSanitizer } from '@angular/platform-browser';
import { AbstractBaseComponent } from '@shared/abstract-base/abstract-base.component';

import { Component as BrComponent } from '@bloomreach/spa-sdk/';
import get from 'lodash/get';

import { ResponsiveService } from '@frk/eds-components';
import { Observable } from 'rxjs';
import { SchemaService } from '@services/schema.service';
import { removePTags } from '@utils/text/string-utils';
import { AppStateService, SiteConfigService } from '@services';
import { Location } from '@angular/common';
import { BiosProfileService } from '@services/bios-profile.service';
import { BiographiesDetails } from 'src/app/types/bios-profile.type';

@Component({
  selector: 'ft-profile',
  templateUrl: './profile.component.html',
})
export class ProfileComponent extends AbstractBaseComponent implements OnInit {
  @Input() component!: BrComponent;
  @Input() page!: Page;
  profile: any;
  profileImage: string;
  socialImage: string;
  summaryLabel: string;
  locationLabel: string;
  tenureLabel: string;
  isHandheld$: Observable<boolean>;
  profileSchema: object;
  biosProfile: BiographiesDetails;
  fetchBiosFromElasticFlag = false;

  constructor(
    private meta: Meta,
    private title: Title,
    private responsiveService: ResponsiveService,
    private sanitizer: DomSanitizer,
    private schemaService: SchemaService,
    private metaService: Meta,
    private location: Location,
    private biosProfileService: BiosProfileService,
    private siteConfigService: SiteConfigService,
    private cdr: ChangeDetectorRef
  ) {
    super();
    this.isHandheld$ = this.responsiveService.isHandheld$();
  }

  /**
   * On init hook
   */
  ngOnInit() {
    this.fetchBiosFromElasticFlag = this.siteConfigService.getGlobalConfig()?.siteConfiguration?.site?.fetchBiosFromElasticFlag;

    if (this.fetchBiosFromElasticFlag) {
      const biosProfileLink = this.location.path().split('/');
      const documentName = biosProfileLink.pop();

      this.biosProfileService
        .getProfileDataFromBios(documentName)
        .subscribe((biodata: BiographiesDetails) => {
          this.biosProfile = biodata;
          this.cdr.detectChanges();
        });
    } else {
      this.setBreadcrumbItems();
      const profileRef = this.component.getModels<DocumentData>()?.document;
      const profileDoc: any = this.page.getContent(profileRef);
      const profileData = this.component.getModels()?.data;
      if ((profileDoc && profileDoc.model) || profileData) {
        this.profile = profileDoc?.getData() || profileData;
        this.profile.overview = removePTags(this.profile.overview);
        const metadata = this.page.getComponent().getModels()?.pageData;
        if (metadata) {
          if (!metadata.title) {
            this.title.setTitle(
              this.profile.fullName +
                (metadata.appendedTitleText
                  ? ' | ' + metadata.appendedTitleText
                  : '')
            );
            if (!metadata.socialTitle) {
              this.metaService.updateTag({
                property: 'og:title',
                content: this.profile.fullName,
              });
            }
          }
          if (!metadata.socialImage) {
            if (this.profile.profileImage) {
              /* when we have a reference to the image, we call getImageVariantUrl method to get
              a fully qualified url  */
              if (this.profile.profileImage.image) {
                this.profileImage = this.getImageVariantUrl(
                  this.profile.profileImage.image,
                  'pmHeadshotBioPage'
                );
                if (!this.socialImage || this.socialImage === '') {
                  this.metaService.updateTag({
                    property: 'og:image',
                    content: this.getImageVariantUrl(
                      this.profile.profileImage.image,
                      'pmHeadshotBioPage'
                    ),
                  });
                }
              }

              /* when we have the complete image url from bloomreach
               * and no modifications are required to it.
               */
              if (
                this.profile.profileImage.widenAsset &&
                !this.profile.cardImage?.widenAsset
              ) {
                this.setImageAndMetaTags(this.profile.profileImage.widenAsset);
              } else if (this.profile.cardImage?.widenAsset) {
                this.setImageAndMetaTags(this.profile.cardImage.widenAsset);
                /*
                 * If higher Quality Card Image exists replace thumbnail image with it
                 */
              }
            } else if (this.profile.webImage) {
              this.profileImage = this.profile.webImage;
              this.metaService.updateTag({
                property: 'og:image',
                content: this.profile.webImage,
              });
            }
          }
        }
      }

      this.setSchema();
    }

    this.initLabels();
  }

  /**
   * Set schema on page
   */
  setSchema(): void {
    // Use schema service to inject into head
    this.profileSchema = SchemaService.personSchema(
      this.profile?.fullName,
      this.profile?.overview,
      this.profileImage
    );
    this.schemaService.insertSchema(
      this.profileSchema,
      'structured-data-profile'
    );
  }

  /**
   * Read labels from resource bundle
   */
  initLabels() {
    const labels = Labels.getLabels(LabelCollection.common);
    if (labels) {
      this.summaryLabel = labels.summary;
      this.locationLabel = labels.location;
      this.tenureLabel = labels.tenure;
    }
  }

  /*
    As part of the upgrade to Bloomreach v14.1, images are returned as fully qualified URLs
    Including additional logic to fetch URL of the image from Bloomreach
  */
  getImageVariantUrl(imageRef: Reference, variant: string): string {
    if (imageRef) {
      const image = this.page.getContent(imageRef)?.getData();
      if (image) {
        const imageVariant = image[variant];
        return imageVariant.links.site.href;
      }
    }
  }

  /*
   * Set Profile Image and add Meta tags
   */
  setImageAndMetaTags(assetJSONStr: string) {
    this.profileImage = JSON.parse(assetJSONStr).url;
    if (!this.socialImage || this.socialImage === '') {
      this.metaService.updateTag({
        property: 'og:image',
        content: JSON.parse(assetJSONStr).url,
      });
    }
  }
}
